// @flow strict

import React from 'react';
import { injectIntl } from 'react-intl';
import { fromJS } from 'immutable';

// Components
import {
    Common,
    InputSearch,
    Pagination,
    PrimaryButton,
    SecondaryButton,
    WarningButton,
    InverseButton,
    Table,
    Toggle,
} from 'components/_ReactUI_V1';

import ConfirmationModal from 'components/Modals/ConfirmationModal';

// Constants
import { ROLES } from 'utils/constants';

// Styles
import {
    Container,
    FilterRow,
    TableContainer,
    RoleButtonGroup,
    RoleDescriptions,
    RoleDescriptionItem,
    RoleName,
    UserCell,
    UserCellName,
    UserCellEmail,
} from './styles';
import { Count, Title } from 'styles/common';

// Types
import type { ImmutableList, IntlType, RoleConstant } from 'types';
import type { ImmutableUser } from 'services/Authentication/types';
import type { UserSearchCriteria } from 'services/User/types';

type Props = {
    intl: IntlType,
    queriedUsers: ImmutableList<ImmutableUser>,
    pageCount: number,
    usersTotal: number,
    usersAreQuerying: boolean,
    onUsersReFetch: (searchCriteria: UserSearchCriteria, page: number) => void,
    changeUserRole: (userId: number, role: string) => void,
    unassignUserRole: (userId: number) => void,
    userIsUpdating: boolean,
};

type State = {
    searchCriteria: UserSearchCriteria,
    pagination: { page: number },
    noRoleOnly: boolean,
    userToUpdate: ?ImmutableUser,
    selectedRole: ?RoleConstant,
};

class ChangeUserRole extends React.Component<Props, State> {
    state = {
        searchCriteria: {
            search: '',
            sortBy: 'email',
            sortOrder: 'asc',
        },
        pagination: { page: 1 },
        noRoleOnly: false,
        userToUpdate: null,
        selectedRole: null,
    };

    handleKeyPress = (event: Object) => {
        if (event && event.key === 'Enter') {
            this.onSearchSubmit();
        }
    };

    onSearchChange = (event: Object) => {
        this.setState((prevState: State) => ({
            searchCriteria: {
                ...prevState.searchCriteria,
                search: event.target.value,
            },
        }));
    };

    onSearchSubmit = () => {
        this.setState(
            { pagination: { page: 1 } },
            () => this.props.onUsersReFetch(this.getSearchCriteria(), this.state.pagination.page)
        );
    };

    onSearchClear = () => {
        this.setState(
            (prevState: State) => ({
                searchCriteria: { ...prevState.searchCriteria, search: '' },
                pagination: { page: 1 },
            }),
            () => this.props.onUsersReFetch(this.getSearchCriteria(), this.state.pagination.page)
        );
    };

    handleNoRoleToggle = () => {
        this.setState(
            (prevState: State) => ({
                noRoleOnly: !prevState.noRoleOnly,
                pagination: { page: 1 },
            }),
            () => this.props.onUsersReFetch(this.getSearchCriteria(), this.state.pagination.page)
        );
    };

    handlePaginationPageSelection = (page: number, total: number) => () => {
        if (page >= 1 && page <= total) {
            this.setState(
                { pagination: { page } },
                () => this.props.onUsersReFetch(this.getSearchCriteria(), this.state.pagination.page)
            );
        }
    };

    handleSortBy = (sortCriteria: { sortBy: string, sortOrder: string }) => {
        this.setState(
            (prevState: State) => ({
                searchCriteria: {
                    ...prevState.searchCriteria,
                    ...sortCriteria,
                },
                pagination: { page: 1 },
            }),
            () => this.props.onUsersReFetch(this.getSearchCriteria(), this.state.pagination.page)
        );
    };

    getSearchCriteria = (): UserSearchCriteria => {
        const criteria: UserSearchCriteria = { ...this.state.searchCriteria };
        if (this.state.noRoleOnly) {
            criteria.noRoleOnly = true;
        }
        return criteria;
    };

    handleRoleChange = (user: ImmutableUser, role: RoleConstant) => {
        if (role === ROLES.ADMIN) {
            // ADMIN requires confirmation
            this.setState({
                userToUpdate: user,
                selectedRole: role,
            });
        } else {
            // PM, SPM, SAM are assigned directly
            this.props.changeUserRole(user.get('id'), role);
        }
    };

    handleConfirmRoleChange = () => {
        const { userToUpdate, selectedRole } = this.state;
        if (userToUpdate && selectedRole) {
            this.props.changeUserRole(userToUpdate.get('id'), selectedRole);
            this.setState({ userToUpdate: null, selectedRole: null });
        }
    };

    handleCancelRoleChange = () => {
        this.setState({ userToUpdate: null, selectedRole: null });
    };

    getTableHeader = () => {
        return [
            {
                label: this.props.intl.formatMessage({
                    id: 'components.ChangeUserRole.table.header.user',
                }),
                id: 'user',
                sortable: true,
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'components.ChangeUserRole.table.header.currentRoles',
                }),
                id: 'currentRoles',
            },
            {
                label: this.props.intl.formatMessage({
                    id: 'components.ChangeUserRole.table.header.assignRole',
                }),
                id: 'assignRole',
            },
        ];
    };

    getRoleDisplayName = (role: RoleConstant): string => {
        switch (role) {
            case ROLES.SAM: return 'AM';
            case ROLES.SPM: return 'PM, SPM';
            default: return role;
        }
    };

    getCurrentRolesDisplay = (currentRoles: Array<RoleConstant>): string => {
        if (currentRoles.length === 0) {
            return this.props.intl.formatMessage({ id: 'components.ChangeUserRole.noRole' });
        }
        // If user has SPM, it implies PM — show "PM + SPM" instead of both separately
        const hasSpm = currentRoles.includes(ROLES.SPM);
        const display = currentRoles
            .filter((r) => !(hasSpm && r === ROLES.PM))
            .map((r) => this.getRoleDisplayName(r));
        return display.join(', ');
    };

    getTableRows = () => {
        // Role options: PM, PM + SPM, AM (SAM), ADMIN
        const roleOptions = [
            { key: ROLES.PM, label: 'PM' },
            { key: ROLES.SPM, label: 'PM + SPM' },
            { key: ROLES.SAM, label: 'AM' },
            { key: ROLES.ADMIN, label: 'ADMIN' },
        ];

        return this.props.queriedUsers
            .map((user: ImmutableUser) => {
                const currentRoles = user.get('roles') ? user.get('roles').toJS() : [];
                const currentRolesDisplay = this.getCurrentRolesDisplay(currentRoles);

                return fromJS({
                    user: (
                        <UserCell>
                            <UserCellName>{user.get('displayName') || '-'}</UserCellName>
                            <UserCellEmail>{user.get('email') || '-'}</UserCellEmail>
                        </UserCell>
                    ),
                    currentRoles: currentRolesDisplay,
                    assignRole: (
                        <RoleButtonGroup>
                            <InverseButton
                                key="unassign"
                                text={this.props.intl.formatMessage({
                                    id: 'components.ChangeUserRole.unassign',
                                })}
                                onClick={() => this.props.unassignUserRole(user.get('id'))}
                                loading={this.props.userIsUpdating}
                                disabled={this.props.userIsUpdating || !currentRoles.length}
                            />
                            {roleOptions.map(({ key, label }) => {
                                const isCurrentRole = currentRoles.includes(key);
                                const hasSpm = currentRoles.includes(ROLES.SPM)
                                const canOverrideSetPm = key === ROLES.PM && hasSpm;
                                if (isCurrentRole && !canOverrideSetPm) {
                                    return (
                                        <SecondaryButton
                                            key={key}
                                            text={label}
                                            disabled
                                        />
                                    );
                                }
                                if (key === ROLES.ADMIN) {
                                    return (
                                        <WarningButton
                                            key={key}
                                            text={label}
                                            onClick={() => this.handleRoleChange(user, key)}
                                            loading={this.props.userIsUpdating}
                                            disabled={this.props.userIsUpdating}
                                        />
                                    );
                                }
                                return (
                                    <PrimaryButton
                                        key={key}
                                        text={label}
                                        onClick={() => this.handleRoleChange(user, key)}
                                        loading={this.props.userIsUpdating}
                                        disabled={this.props.userIsUpdating}
                                    />
                                );
                            })}
                        </RoleButtonGroup>
                    ),
                });
            })
            .toJS();
    };

    renderConfirmationModal = () => {
        const { userToUpdate, selectedRole } = this.state;
        if (!userToUpdate || selectedRole !== ROLES.ADMIN) return null;

        const userName = userToUpdate.get('displayName') || userToUpdate.get('email');

        return (
            <ConfirmationModal
                message={this.props.intl.formatMessage(
                    { id: 'components.ChangeUserRole.confirmAdminMessage' },
                    { userName }
                )}
                onCancel={this.handleCancelRoleChange}
                onConfirm={this.handleConfirmRoleChange}
                title={this.props.intl.formatMessage({
                    id: 'components.ChangeUserRole.confirmAdminTitle',
                })}
            />
        );
    };

    render() {
        const { usersAreQuerying, pageCount, usersTotal } = this.props;

        return (
            <Container>
                <Common.Row
                    justifyContent="space-between"
                    alignItems="baseline"
                    style={{ gap: '8px', flexGrow: 0 }}
                >
                    <Title style={{ flexShrink: 0 }}>
                        {this.props.intl.formatMessage({
                            id: 'components.ChangeUserRole.title',
                        })}
                        {usersTotal > 0 && <Count>({usersTotal})</Count>}
                    </Title>
                    <InputSearch
                        handleOnClear={this.onSearchClear}
                        handleOnClick={this.onSearchSubmit}
                        onChange={this.onSearchChange}
                        onKeyDown={this.handleKeyPress}
                        placeholder={this.props.intl.formatMessage({
                            id: 'components.ChangeUserRole.searchPlaceholder',
                        })}
                        value={this.state.searchCriteria.search}
                        withSearch={this.state.searchCriteria.search.length > 0}
                    />

                    <FilterRow>
                        <Toggle
                            checked={this.state.noRoleOnly}
                            label={this.props.intl.formatMessage({
                                id: 'components.ChangeUserRole.noRoleOnly',
                            })}
                            onClickHandler={this.handleNoRoleToggle}
                        />
                    </FilterRow>
                </Common.Row>


                <RoleDescriptions>
                    <RoleDescriptionItem>
                        <RoleName>PM</RoleName>{' — '}
                        {this.props.intl.formatMessage({ id: 'components.ChangeUserRole.roleDescriptions.pm' })}
                    </RoleDescriptionItem>
                    <RoleDescriptionItem>
                        <RoleName>SPM</RoleName>{' — '}
                        {this.props.intl.formatMessage({ id: 'components.ChangeUserRole.roleDescriptions.spm' })}
                    </RoleDescriptionItem>
                    <RoleDescriptionItem>
                        <RoleName>AM</RoleName>{' — '}
                        {this.props.intl.formatMessage({ id: 'components.ChangeUserRole.roleDescriptions.am' })}
                    </RoleDescriptionItem>
                    <RoleDescriptionItem>
                        <RoleName>ADMIN</RoleName>{' — '}
                        {this.props.intl.formatMessage({ id: 'components.ChangeUserRole.roleDescriptions.admin' })}
                    </RoleDescriptionItem>
                </RoleDescriptions>

                <Table
                    currentSorting={{
                        sortBy: this.state.searchCriteria.sortBy,
                        sortOrder: this.state.searchCriteria.sortOrder,
                    }}
                    header={this.getTableHeader()}
                    onSortBy={this.handleSortBy}
                    rows={this.getTableRows()}
                    overlay={usersAreQuerying && <div />}
                    loading={usersAreQuerying}
                    footerMessage={
                        !usersAreQuerying &&
                        this.props.queriedUsers.size === 0 &&
                        this.props.intl.formatMessage({
                            id: 'components.ChangeUserRole.noResults',
                        })
                    }
                />
                <Pagination
                    currentPage={this.state.pagination.page}
                    isLoading={usersAreQuerying}
                    onPageSelection={this.handlePaginationPageSelection}
                    pagesTotal={pageCount}
                    summaryPrefix={this.props.intl.formatMessage({
                        id: 'components.ChangeUserRole.paginationPrefix',
                    })}
                    summaryJoinner={this.props.intl.formatMessage({
                        id: 'components.ChangeUserRole.paginationJoinner',
                    })}
                />

                {this.renderConfirmationModal()}
            </Container>
        );
    }
}

export default injectIntl(ChangeUserRole);
